import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  Brain,
  MessageSquare,
  BarChart3,
  TrendingUp,
  TrendingDown,
  Zap,
  Settings,
  Sparkles,
  Bot,
  Lightbulb,
  Target,
  AlertTriangle,
  CheckCircle,
  ArrowUpRight,
  ArrowLeft,
  Users,
  DollarSign,
  Clock,
  Briefcase
} from 'lucide-react'
// Consolidated CSS is imported globally

const AIAssistant = () => {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('overview')
  const [chatInput, setChatInput] = useState('')
  const [messages, setMessages] = useState([])

  const handleQuickAction = (action) => {
    const quickActions = {
      revenue: "Show me our revenue analysis and trends for this quarter",
      customers: "Analyze our customer data and identify key insights",
      projects: "What's the current status of our active projects?",
      forecast: "Provide a sales forecast for the next 3 months"
    }
    
    setChatInput(quickActions[action])
    // Auto-send the message
    setTimeout(() => {
      handleSendMessage(quickActions[action])
    }, 100)
  }

  const handleSuggestion = (suggestion) => {
    const suggestions = {
      revenue: "Show revenue trends",
      customers: "Customer analysis", 
      projects: "Project status",
      forecast: "Growth forecast"
    }
    
    setChatInput(suggestions[suggestion])
    setTimeout(() => {
      handleSendMessage(suggestions[suggestion])
    }, 100)
  }

  const handleSendMessage = (message = null) => {
    const messageText = message || chatInput.trim()
    if (!messageText) return

    // Add user message
    const userMessage = {
      id: Date.now(),
      type: 'user',
      text: messageText,
      timestamp: new Date()
    }
    
    setMessages(prev => [...prev, userMessage])
    setChatInput('')

    // Simulate AI response
    setTimeout(() => {
      const aiResponse = {
        id: Date.now() + 1,
        type: 'ai',
        text: generateAIResponse(messageText),
        timestamp: new Date()
      }
      setMessages(prev => [...prev, aiResponse])
    }, 1000)
  }

  const generateAIResponse = (query) => {
    const responses = {
      revenue: "Based on your current data, I can see your revenue has grown 15% this quarter. Your top-performing products are generating 68% of total revenue. Would you like me to dive deeper into any specific area?",
      customers: "I've analyzed your customer data and found 3 key insights: 1) Your customer acquisition cost decreased by 12%, 2) Customer lifetime value increased by 23%, and 3) 5 customers are showing early churn signals. Should I provide detailed recommendations?",
      projects: "You have 8 active projects with an average completion rate of 78%. 3 projects are ahead of schedule, 2 are on track, and 3 need attention. The main bottleneck is resource allocation. Would you like optimization suggestions?",
      forecast: "Based on current trends and historical data, I predict 18% revenue growth over the next 3 months. Key factors include seasonal patterns, market conditions, and your current pipeline. Would you like to see the detailed forecast breakdown?"
    }
    
    // Simple keyword matching for demo
    if (query.toLowerCase().includes('revenue')) return responses.revenue
    if (query.toLowerCase().includes('customer')) return responses.customers
    if (query.toLowerCase().includes('project')) return responses.projects
    if (query.toLowerCase().includes('forecast')) return responses.forecast
    
    return "I understand you're asking about: " + query + ". Let me analyze your business data to provide you with the most relevant insights. This might take a moment..."
  }

  return (
    <div className="sales-dashboard">
      {/* Sub-section Navigation */}
      <div className="sales-tabs">
        <button 
          className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <BarChart3 size={16} />
          <span>Overview</span>
        </button>
        <button 
          className={`tab-button ${activeTab === 'chat' ? 'active' : ''}`}
          onClick={() => setActiveTab('chat')}
        >
          <MessageSquare size={16} />
          <span>AI Chat</span>
        </button>
        <button 
          className={`tab-button ${activeTab === 'insights' ? 'active' : ''}`}
          onClick={() => setActiveTab('insights')}
        >
          <Lightbulb size={16} />
          <span>Insights</span>
        </button>
        <button 
          className={`tab-button ${activeTab === 'predictions' ? 'active' : ''}`}
          onClick={() => setActiveTab('predictions')}
        >
          <TrendingUp size={16} />
          <span>Predictions</span>
        </button>
        <button 
          className={`tab-button ${activeTab === 'automation' ? 'active' : ''}`}
          onClick={() => setActiveTab('automation')}
        >
          <Zap size={16} />
          <span>Automation</span>
        </button>
      </div>

      <div
        className="page-header d-flex flex-wrap justify-content-between align-items-start gap-3"
        style={{ textAlign: 'left' }}
      >
        <div className="page-title" style={{ textAlign: 'left', alignItems: 'flex-start' }}>
          <h1 style={{ textAlign: 'left', margin: '0 0 4px 0' }}>AI Assistant</h1>
          <p style={{ textAlign: 'left', margin: 0 }}>Your intelligent business companion powered by Gen AI</p>
        </div>
        <div className="d-flex flex-wrap align-items-center gap-2">
          <button
            type="button"
            className="btn btn-outline-primary d-inline-flex align-items-center gap-2"
            onClick={() => navigate('/dashboard')}
          >
            <ArrowLeft size={16} />
            Back to Dashboard
          </button>
          <div className="ai-status-badge">
            <div className="status-indicator active">
              <div className="status-dot"></div>
              <span>AI Active</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="tab-content">
          {/* AI Status Overview */}

          {/* AI Capabilities Grid */}
          <div className="dashboard-metrics-grid">
            <div className="metric-card revenue">
              <div className="metric-header">
                <div className="metric-icon">
                  <Sparkles size={16} />
                </div>
                <div className="metric-trend positive">
                  <span>Active</span>
                </div>
              </div>
              <div className="metric-value">12</div>
              <div className="metric-title">AI Models Running</div>
              <div className="metric-subtitle">Real-time analysis</div>
            </div>

            <div className="metric-card customers">
              <div className="metric-header">
                <div className="metric-icon">
                  <Lightbulb size={16} />
                </div>
                <div className="metric-trend positive">
                  <span>+47</span>
                </div>
              </div>
              <div className="metric-value">1,247</div>
              <div className="metric-title">Insights Generated</div>
              <div className="metric-subtitle">This month</div>
            </div>

            <div className="metric-card churn">
              <div className="metric-header">
                <div className="metric-icon">
                  <Target size={16} />
                </div>
                <div className="metric-trend positive">
                  <span>94.2%</span>
                </div>
              </div>
              <div className="metric-value">94.2%</div>
              <div className="metric-title">Prediction Accuracy</div>
              <div className="metric-subtitle">ML model performance</div>
            </div>

            <div className="metric-card burn">
              <div className="metric-header">
                <div className="metric-icon">
                  <Zap size={16} />
                </div>
                <div className="metric-trend positive">
                  <span>+23</span>
                </div>
              </div>
              <div className="metric-value">156</div>
              <div className="metric-title">Automations Active</div>
              <div className="metric-subtitle">Workflow optimizations</div>
            </div>
          </div>

          {/* Recent AI Insights */}
          <div className="dashboard-action-section">
            <div className="dashboard-action-items">
              <div className="section-header">
                <h3>Recent AI Insights</h3>
                <span className="item-count">8 insights</span>
              </div>
              <div className="action-list">
                <div className="action-item urgent">
                  <div className="action-icon">
                    <AlertTriangle size={16} />
                  </div>
                  <div className="action-content">
                    <div className="action-title">Revenue Forecast Alert</div>
                    <div className="action-description">AI predicts 15% revenue increase next quarter</div>
                  </div>
                  <div className="action-time">2 hours ago</div>
                </div>
                <div className="action-item medium">
                  <div className="action-icon">
                    <Users size={16} />
                  </div>
                  <div className="action-content">
                    <div className="action-title">Customer Churn Risk</div>
                    <div className="action-description">3 high-value customers at risk of churning</div>
                  </div>
                  <div className="action-time">4 hours ago</div>
                </div>
                <div className="action-item low">
                  <div className="action-icon">
                    <Lightbulb size={16} />
                  </div>
                  <div className="action-content">
                    <div className="action-title">Process Optimization</div>
                    <div className="action-description">AI suggests 23% efficiency improvement in procurement</div>
                  </div>
                  <div className="action-time">1 day ago</div>
                </div>
              </div>
            </div>

            <div className="dashboard-team-activity">
              <div className="section-header">
                <h3>AI Recommendations</h3>
                <span className="activity-count">12 suggestions</span>
              </div>
              <div className="activity-list">
                <div className="activity-item">
                  <div className="activity-avatar">🤖</div>
                  <div className="activity-content">
                    <div className="activity-text">
                      <strong>AI Assistant</strong> recommends optimizing project timelines
                    </div>
                    <div className="activity-time">30 minutes ago</div>
                  </div>
                </div>
                <div className="activity-item">
                  <div className="activity-avatar">📊</div>
                  <div className="activity-content">
                    <div className="activity-text">
                      <strong>Analytics AI</strong> suggests new pricing strategy for Q1
                    </div>
                    <div className="activity-time">2 hours ago</div>
                  </div>
                </div>
                <div className="activity-item">
                  <div className="activity-avatar">⚡</div>
                  <div className="activity-content">
                    <div className="activity-text">
                      <strong>Automation AI</strong> identified 5 workflow improvements
                    </div>
                    <div className="activity-time">4 hours ago</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'chat' && (
        <div className="tab-content">
          {/* Quick Actions */}
          <div className="ai-quick-actions">
            <div className="quick-actions-header">
              <h3>Quick Actions</h3>
              <p>Get instant insights with these common queries</p>
            </div>
            <div className="quick-actions-grid">
              <button className="quick-action-btn" onClick={() => handleQuickAction('revenue')}>
                <DollarSign size={20} />
                <span>Revenue Analysis</span>
              </button>
              <button className="quick-action-btn" onClick={() => handleQuickAction('customers')}>
                <Users size={20} />
                <span>Customer Insights</span>
              </button>
              <button className="quick-action-btn" onClick={() => handleQuickAction('projects')}>
                <Briefcase size={20} />
                <span>Project Status</span>
              </button>
              <button className="quick-action-btn" onClick={() => handleQuickAction('forecast')}>
                <TrendingUp size={20} />
                <span>Sales Forecast</span>
              </button>
            </div>
          </div>

          {/* AI Chat Section */}
          <div className="ai-chat-section">
            <div className="chat-header">
              <div className="chat-title">
                <Bot size={20} />
                <span>AI Business Assistant</span>
              </div>
              <div className="chat-status">
                <div className="status-dot active"></div>
                <span>Online</span>
              </div>
            </div>
            <div className="chat-content">
              <div className="chat-messages">
                {/* Welcome messages - only show if no conversation yet */}
                {messages.length === 0 && (
                  <>
                    <div className="message ai-message">
                      <div className="message-avatar">🤖</div>
                      <div className="message-content">
                        <div className="message-text">
                          <strong>Welcome to your AI Business Assistant!</strong><br/>
                          I'm here to help you analyze your business data and provide intelligent insights.
                        </div>
                        <div className="message-time">Just now</div>
                      </div>
                    </div>
                    <div className="message ai-message">
                      <div className="message-avatar">🤖</div>
                      <div className="message-content">
                        <div className="message-text">
                          <strong>I can help you with:</strong><br/>
                          📊 Business analytics and insights<br/>
                          🔮 Predictive forecasting<br/>
                          ⚡ Process optimization<br/>
                          ⚠️ Risk assessment<br/>
                          💡 Strategic recommendations
                        </div>
                        <div className="message-time">Just now</div>
                      </div>
                    </div>
                    <div className="message ai-message">
                      <div className="message-avatar">🤖</div>
                      <div className="message-content">
                        <div className="message-text">
                          <strong>Try asking me:</strong><br/>
                          • "What's our revenue trend this quarter?"<br/>
                          • "Which customers are at risk of churning?"<br/>
                          • "How can we optimize our project timelines?"<br/>
                          • "What are our top growth opportunities?"
                        </div>
                        <div className="message-time">Just now</div>
                      </div>
                    </div>
                  </>
                )}
                
                {/* Dynamic messages */}
                {messages.map((message) => (
                  <div key={message.id} className={`message ${message.type === 'ai' ? 'ai-message' : 'user-message'}`}>
                    <div className="message-avatar">
                      {message.type === 'ai' ? '🤖' : '👤'}
                    </div>
                    <div className="message-content">
                      <div className="message-text">{message.text}</div>
                      <div className="message-time">
                        {message.timestamp.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Suggested Questions */}
              <div className="suggested-questions">
                <div className="suggestions-header">
                  <span>💡 Suggested questions:</span>
                </div>
                <div className="suggestions-list">
                  <button className="suggestion-chip" onClick={() => handleSuggestion('revenue')}>
                    Show revenue trends
                  </button>
                  <button className="suggestion-chip" onClick={() => handleSuggestion('customers')}>
                    Customer analysis
                  </button>
                  <button className="suggestion-chip" onClick={() => handleSuggestion('projects')}>
                    Project status
                  </button>
                  <button className="suggestion-chip" onClick={() => handleSuggestion('forecast')}>
                    Growth forecast
                  </button>
                </div>
              </div>

              <div className="chat-input">
                <div className="input-container">
                  <Brain size={16} className="input-icon" />
                  <input 
                    type="text" 
                    placeholder="Ask me anything about your business..."
                    className="chat-input-field"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  />
                  <button className="send-button" onClick={handleSendMessage}>
                    <ArrowUpRight size={16} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'insights' && (
        <div className="tab-content">
          <div className="ai-insights-content">
            <div className="insights-header">
              <h2>AI Business Insights</h2>
              <p>Intelligent analysis of your business data and performance</p>
            </div>
            <div className="insights-grid">
              <div className="insight-card">
                <div className="insight-icon">
                  <DollarSign size={24} />
                </div>
                <div className="insight-content">
                  <h3>Revenue Optimization</h3>
                  <p>AI identified 3 opportunities to increase revenue by 18%</p>
                  <div className="insight-impact">+$45,000 potential</div>
                </div>
              </div>
              <div className="insight-card">
                <div className="insight-icon">
                  <Users size={24} />
                </div>
                <div className="insight-content">
                  <h3>Customer Intelligence</h3>
                  <p>Behavioral analysis reveals 5 high-value customer segments</p>
                  <div className="insight-impact">+23% retention</div>
                </div>
              </div>
              <div className="insight-card">
                <div className="insight-icon">
                  <Clock size={24} />
                </div>
                <div className="insight-content">
                  <h3>Process Efficiency</h3>
                  <p>AI found bottlenecks in 4 key business processes</p>
                  <div className="insight-impact">+31% efficiency</div>
                </div>
              </div>
              <div className="insight-card">
                <div className="insight-icon">
                  <Target size={24} />
                </div>
                <div className="insight-content">
                  <h3>Risk Management</h3>
                  <p>Early warning system identified 2 potential risks</p>
                  <div className="insight-impact">-67% risk exposure</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'predictions' && (
        <div className="tab-content">
          <div className="ai-predictions-content">
            <div className="predictions-header">
              <h2>AI Predictions & Forecasting</h2>
              <p>Machine learning models predicting future business outcomes</p>
            </div>
            <div className="predictions-grid">
              <div className="prediction-card">
                <div className="prediction-header">
                  <h3>Revenue Forecast</h3>
                  <div className="confidence-badge">94% Confidence</div>
                </div>
                <div className="prediction-value">$2.4M</div>
                <div className="prediction-period">Next Quarter</div>
                <div className="prediction-trend">
                  <TrendingUp size={16} />
                  <span>+18% vs current quarter</span>
                </div>
              </div>
              <div className="prediction-card">
                <div className="prediction-header">
                  <h3>Customer Churn</h3>
                  <div className="confidence-badge">87% Confidence</div>
                </div>
                <div className="prediction-value">3.2%</div>
                <div className="prediction-period">Next Month</div>
                <div className="prediction-trend">
                  <TrendingDown size={16} />
                  <span>-0.8% vs current month</span>
                </div>
              </div>
              <div className="prediction-card">
                <div className="prediction-header">
                  <h3>Project Completion</h3>
                  <div className="confidence-badge">91% Confidence</div>
                </div>
                <div className="prediction-value">23 days</div>
                <div className="prediction-period">Average delay</div>
                <div className="prediction-trend">
                  <Clock size={16} />
                  <span>On schedule</span>
                </div>
              </div>
              <div className="prediction-card">
                <div className="prediction-header">
                  <h3>Market Opportunity</h3>
                  <div className="confidence-badge">89% Confidence</div>
                </div>
                <div className="prediction-value">$1.2M</div>
                <div className="prediction-period">Untapped potential</div>
                <div className="prediction-trend">
                  <Target size={16} />
                  <span>New segments identified</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'automation' && (
        <div className="tab-content">
          <div className="ai-automation-content">
            <div className="automation-header">
              <h2>AI Automation Center</h2>
              <p>Intelligent workflows and automated business processes</p>
            </div>
            <div className="automation-grid">
              <div className="automation-card">
                <div className="automation-icon">
                  <Zap size={24} />
                </div>
                <div className="automation-content">
                  <h3>Invoice Processing</h3>
                  <p>AI automatically extracts and processes vendor invoices</p>
                  <div className="automation-status active">Active</div>
                  <div className="automation-savings">Saves 15 hours/week</div>
                </div>
              </div>
              <div className="automation-card">
                <div className="automation-icon">
                  <Users size={24} />
                </div>
                <div className="automation-content">
                  <h3>Lead Scoring</h3>
                  <p>Automatically scores and prioritizes sales leads</p>
                  <div className="automation-status active">Active</div>
                  <div className="automation-savings">+34% conversion rate</div>
                </div>
              </div>
              <div className="automation-card">
                <div className="automation-icon">
                  <Clock size={24} />
                </div>
                <div className="automation-content">
                  <h3>Timesheet Validation</h3>
                  <p>AI validates and approves timesheet entries</p>
                  <div className="automation-status pending">Pending Setup</div>
                  <div className="automation-savings">Will save 8 hours/week</div>
                </div>
              </div>
              <div className="automation-card">
                <div className="automation-icon">
                  <Target size={24} />
                </div>
                <div className="automation-content">
                  <h3>Risk Monitoring</h3>
                  <p>Continuous monitoring of business risks and alerts</p>
                  <div className="automation-status active">Active</div>
                  <div className="automation-savings">Prevents 3 risks/month</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AIAssistant
